"use client"

import { Button } from "@/components/atoms"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import clsx from "clsx"
import { BaseHit, Hit } from "instantsearch.js"
import Image from "next/image"
import { ButtonAddToCart } from "@/components/custom/Product/AddCart"
import cardIcon from "@/icons/card.svg"
import { useState } from "react"
import { useScreenSize } from "@/hooks/useScreenSize"
import { WishlistButton } from "@/components/cells/WishlistButton/WishlistButton"
import { useAppContext } from "@/lib/context/app-context"
import { BarebonesProductCard } from "@/components/skins/barebones/BarebonesProductCard"
import { isBarebonesTheme } from "@/lib/theme/storefront-theme"
import { useStorefrontTheme } from "@/lib/theme/storefront-theme-context"

export const ProductCard = ({
  product = {},
  api_product,
  locale,
  showAddToCartButton = true,
  showVariantCount = false,
  className,
}: {
  product: Hit<HttpTypes.StoreProduct> | Partial<Hit<BaseHit>>
  api_product?: HttpTypes.StoreProduct | null
  locale: string
  showAddToCartButton?: boolean
  showVariantCount?: boolean
  className?: string
}) => {
  const { t } = useTranslate();
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const screenSize = useScreenSize()
  const { user, wishlist } = useAppContext()
  const theme = useStorefrontTheme()

  if (!api_product) {
    return <></>
  }

  if (isBarebonesTheme(theme)) {
    return (
      <BarebonesProductCard
        product={api_product as HttpTypes.StoreProduct}
        locale={locale}
      />
    )
  }

  const { cheapestVariant, cheapestPrice } = getProductPrice({
    product: api_product! as HttpTypes.StoreProduct,
  })

  const isSmallScreen = screenSize === 'sm'
  const isAvailable = cheapestVariant && cheapestPrice?.calculated_price

  return (
    <div
      className={clsx(
        "relative group rounded-sm flex flex-col justify-between w-full",
        className
      )}
    >
      <div className="relative w-full h-full bg-primary aspect-square">
        {user && (
          <div className="absolute right-2 top-2 z-10">
            <WishlistButton
              productId={api_product.id}
              product={api_product as HttpTypes.StoreProduct}
              wishlist={wishlist}
              user={user}
            />
          </div>
        )}
        <LocalizedClientLink href={`/products/${product.handle}`}>
          <div className="overflow-hidden rounded-sm w-full h-full flex justify-center align-center ">
            {product.thumbnail ? (
              <Image
                src={decodeURIComponent(product.thumbnail)}
                alt={t("product_card.product_placeholder")}
                width={360}
                height={360}
                priority
                className="object-cover aspect-square w-full object-center h-full lg:group-hover:-mt-14 transition-all duration-300 rounded-xs"
              />
            ) : (
              <Image
                src="/images/placeholder.svg"
                alt={t("product_card.product_placeholder")}
                width={100}
                height={100}
              />
            )}
          </div>
        </LocalizedClientLink>
        <LocalizedClientLink href={`/products/${product.handle}`}>
          <Button className="absolute rounded-sm bg-action text-action-on-primary h-auto lg:h-[48px] lg:group-hover:block hidden w-full uppercase bottom-1 z-10">
            {t("product_card.see_more")}
          </Button>
        </LocalizedClientLink>
      </div>
      <div className="flex flex-row justify-between align-top p-3 gap-2">
        <LocalizedClientLink href={`/products/${product.handle}`} className="flex-grow overflow-hidden">
          <div className="w-full">
            <h3 className="font-bakbak font-normal text-[20px] tracking-[-0.2px] leading-[28px] text-[#0B0F0E] whitespace-nowrap text-ellipsis overflow-hidden">{product.title}</h3>
            <div className="flex items-center gap-2 mt-2 justify-between whitespace-nowrap">
              <p className="font-bakbak font-normal text-[20px] tracking-[-0.2px] leading-[28px] text-[#0B0F0E]">{cheapestPrice?.calculated_price}</p>
              {cheapestPrice?.calculated_price !==
                cheapestPrice?.original_price && (
                  <p className="text-[16px] font-normal flex-shrink-0 text-gray-500 line-through">
                    {cheapestPrice?.original_price}
                  </p>
                )}
              {showVariantCount && !isSmallScreen ? (
                (api_product.variants?.length || 0) > 1 ? (
                  <p className="text-[16px] font-normal flex-shrink-0 text-gray-500 mt-1">
                    {t("product_card.variants", {
                      count: api_product.variants?.length || 0,
                    })}
                  </p>
                ) : (
                  <p className="text-[16px] font-normal flex-shrink-0 text-gray-500 mt-1">
                    {t("product_card.variants1")}
                  </p>
                )
              ) : null}
            </div>
            {showVariantCount && isSmallScreen ? (
              (api_product.variants?.length || 0) > 1 ? (
                <p className="text-[16px] font-normal flex-shrink-0 text-gray-500 mt-1">
                  {t("product_card.variants", {
                    count: api_product.variants?.length || 0,
                  })}
                </p>
              ) : (
                <p className="text-[16px] font-normal flex-shrink-0 text-gray-500 mt-1">
                  {t("product_card.variants1")}
                </p>
              )
            ) : null}
          </div>
        </LocalizedClientLink>

        {showAddToCartButton && (
          <ButtonAddToCart
            variant={cheapestVariant}
            title={product.title}
            locale={locale}
            setLoading={setIsAddingToCart}
            disabled={!isAvailable}
            className={
              "shrink-0 w-[68px] h-[42px] rounded-lg bg-[#0B0A09] hover:bg-stone-800 flex flex-row items-center justify-center " +
              (isAvailable ? "text-white" : "text-white-400")
            }
          >
            <Image src={cardIcon} width={20} height={20} alt="" />
          </ButtonAddToCart>
        )}

      </div>
    </div>
  )
}
